import PropTypes from 'prop-types';
import React from 'react';
import _classNames from 'clsx';
import Icon from '../icons/Icon/Icon';

/**
  A button that, when clicked, expands a container below for further information or interactions.
  Curretly used in the Dashboard filter sidebar.
*/
class Accordion extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      buttonDisabled: false,
      open: null,
    };

    this.accordionContainer = React.createRef();
    this.accordionHeightCalculator = React.createRef();
  }

  componentDidMount() {
    const { defaultOpen } = this.props;
    const container = this.accordionContainer.current;
    if (defaultOpen) {
      this.setState({ open: true });
      const containerContentHeight = container.scrollHeight;
      container.style.height = `${containerContentHeight}px`;
    } else {
      this.setState({ open: false });
      container.style.height = '0px';
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { updateAccordionHeight, updateAccordionHeightCallback } = this.props;
    const { open } = this.state;

    if ((open || prevState.open) && !prevProps.updateAccordionHeight && updateAccordionHeight) {
      const container = this.accordionContainer.current;
      container.addEventListener('transitionend', this.onTransitionEnd);
      this.expandAccordion();
      updateAccordionHeightCallback();
    }
  }

  onTransitionEnd = () => {
    const container = this.accordionContainer.current;
    container.removeEventListener('transitionend', this.onTransitionEnd);
    this.setState({ buttonDisabled: false });
  };

  handleAccordionClick = () => {
    const { accordionToggleCallback } = this.props;
    const { open } = this.state;

    this.setState({ buttonDisabled: true }, () => {
      const container = this.accordionContainer.current;
      container.addEventListener('transitionend', this.onTransitionEnd);

      if (open) {
        this.collapseAccordion();
      } else {
        this.expandAccordion();
      }

      if (accordionToggleCallback) {
        accordionToggleCallback({ open });
      }
    });
  };

  collapseAccordion = () => {
    this.setState({ open: false });
    const container = this.accordionContainer.current;
    const containerHeight = container.scrollHeight;
    const containerTransition = container.style.transition;

    // temporarily disable css transitions
    container.style.transition = '';

    // as soon as the previous style change has taken effect, explicitly set the height and enable the transition style
    this.setTransition(containerHeight, containerTransition);

    // as soon as the previous style change has taken effect, have the element transition to height: 0
    const shrinkTimer = window.requestAnimationFrame(() => {
      container.style.height = '0px';
    });
    return () => window.cancelAnimationFrame(shrinkTimer);
  };

  setTransition = (containerHeight, containerTransition) => {
    const container = this.accordionContainer.current;
    const setTransitionTimer = window.requestAnimationFrame(() => {
      container.style.height = `${containerHeight}px`;
      container.style.transition = containerTransition;
    });
    return () => window.cancelAnimationFrame(setTransitionTimer);
  };

  expandAccordion = () => {
    this.setState({ open: true }, () => {
      const container = this.accordionContainer.current;
      const heightCalculator = this.accordionHeightCalculator.current;
      const containerHeight = heightCalculator.scrollHeight;

      // explicitly setting the height will trigger the transition
      container.style.height = `${containerHeight}px`;
    });
  };

  render() {
    const { children, footer, header, iconLeft, isFiltered } = this.props;
    const { buttonDisabled, open } = this.state;

    let iconRight = 'Plus--shrunk';
    let iconRightColor = '#8008F7';
    let iconLeftColor = '#3c4667';
    if (open) {
      iconRight = 'Minus--shrunk';
      iconRightColor = '#545d79';
      iconLeftColor = '#8008F7';
    }
    if (!open && isFiltered) {
      iconRight = 'Chevron--single--south';
      iconRightColor = '#545d79';
      iconLeftColor = '#8008F7';
    }

    return (
      <div className="Accordion">
        <button
          className="Accordion__button Button--unstyled"
          disabled={buttonDisabled}
          onClick={this.handleAccordionClick}
          type="button"
        >
          <div className="Accordion__button--content">
            {iconLeft && (
              <Icon
                classes="Accordion__icon-left"
                name={iconLeft}
                svgProps={{ style: { fill: iconLeftColor } }}
              />
            )}
            {header && (
              <div
                className={_classNames('Accordion__header', {
                  'Accordion__header--open': open,
                  'Accordion__header--filtered': isFiltered,
                })}
              >
                {header}
              </div>
            )}
            <Icon
              classes="Accordion__icon-right"
              name={iconRight}
              svgProps={{ style: { fill: iconRightColor } }}
            />
          </div>
        </button>
        <div className="Accordion__container" ref={this.accordionContainer}>
          <div className="Accordion__height-calculator" ref={this.accordionHeightCalculator}>
            {children}
            {footer && <div className="Accordion__footer">{footer}</div>}
          </div>
        </div>
      </div>
    );
  }
}

Accordion.propTypes = {
  /**
    An optional callback function that will be called when the accordion button is clicked.
    It receives the boolean value of the accordion's "open" state as an argument in the
    callback function.
  */
  accordionToggleCallback: PropTypes.func,
  /**
    The elements rendered inside the expandable container
  */
  children: PropTypes.node.isRequired,
  /**
    Optionally set the accordion to initially render in an open state
  */
  defaultOpen: PropTypes.bool,
  /**
    Elements optionally rendered inside a footer in the expandable container
  */
  footer: PropTypes.node,
  /**
    The main button text or accordion title (always visible)
  */
  header: PropTypes.string.isRequired,
  /**
    Optionally render an `Icon` next to the accordion header
  */
  iconLeft: PropTypes.string,
  /**
    Set to true to reflect the state where something within the accordion container has been
    modified (when a filter has been applied; this includes style changes to the accordion
    button when in a closed state)
  */
  isFiltered: PropTypes.bool,
  /**
    When changed from false to true (i.e. based on a parent component's state) it will allow the
    accordion container to expand or contract to accommodate resized internal content
  */
  updateAccordionHeight: PropTypes.bool,
  /**
    When the accordion container's height is updated, this callback function runs after the height
    update transition finishes.
    (Used in the story implementation to reset the parent component's state)
  */
  updateAccordionHeightCallback: PropTypes.func,
};

Accordion.defaultProps = {
  defaultOpen: false,
  accordionToggleCallback: undefined,
  footer: undefined,
  iconLeft: undefined,
  isFiltered: undefined,
  updateAccordionHeight: undefined,
  updateAccordionHeightCallback: undefined,
};

export default Accordion;
